import $i18n from 'panda-i18n';
import * as React from 'react';
import cx from 'classnames';
import { guid, useGuid } from '@/utils/use-guid';
import { CnButton } from '@/components/cn-button';
import { CnActionSheet } from '@/components/cn-action-sheet';
import { getWidth, MORE_BUTTON_WIDTH } from './utils';
import './cn-button-group.scss';
/**
 * @category 组件
 * @description 按钮组，用于排列多个按钮
 */
const CnButtonGroup = (props) => {
    const { children, fullWidth, size = 'medium', isReverse, containerId, hiddenIdx: propsHiddenIdx, shape, } = props;
    const [hiddenIdx, setHiddenIdx] = React.useState(propsHiddenIdx || 0);
    const containerUid = useGuid('tab-scroll-');
    const curContainerId = containerId || containerUid;
    const buttonWidthMap = React.useRef({});
    const { buttonChildren, hiddenChildren } = React.useMemo(() => {
        const newHiddenChildren = [];
        let newButtonChildren = [];
        React.Children.forEach(children, (item, idx) => {
            if (React.isValidElement(item)) {
                if (hiddenIdx !== 0 && idx >= hiddenIdx) {
                    newHiddenChildren.push(item);
                }
                else {
                    newButtonChildren.push(item);
                }
            }
        });
        if (isReverse) {
            newButtonChildren = newButtonChildren.reverse();
        }
        newButtonChildren = React.Children.map(newButtonChildren, (item, idx) => {
            const isLast = newButtonChildren.length === idx + 1;
            let type = item?.props.type;
            if (isLast) {
                if (shape === 'card') {
                    type = 'secondary';
                }
                else {
                    type = 'primary';
                }
            }
            const uid = guid('cn-ui-m-button-group-idx-');
            return React.cloneElement(item, { key: uid, id: uid, fullWidth, size, type });
        });
        return {
            buttonChildren: newButtonChildren,
            hiddenChildren: newHiddenChildren,
        };
    }, [children, fullWidth, hiddenIdx, isReverse, shape, size]);
    React.useEffect(() => {
        if (propsHiddenIdx)
            return;
        const containerWidth = getWidth(curContainerId);
        let totalWidth = 0;
        const orderedButtonChildren = isReverse
            ? buttonChildren.reverse()
            : buttonChildren;
        React.Children.forEach(orderedButtonChildren, (item, idx) => {
            const uid = item.props.id;
            if (uid) {
                let itemWidth = getWidth(uid);
                if (!itemWidth)
                    totalWidth -= 8;
                if (idx !== 0) {
                    itemWidth += 8;
                }
                buttonWidthMap.current[idx] = itemWidth;
                totalWidth += itemWidth;
            }
        });
        const hiddenButtonWidth = MORE_BUTTON_WIDTH[size];
        // overflow 开始进行隐藏
        if (totalWidth > containerWidth) {
            let displayWidth = 0;
            let lastDisplayIdx = 0;
            // 只要显示的宽度 + more按钮宽度
            while (displayWidth +
                hiddenButtonWidth +
                buttonWidthMap.current[lastDisplayIdx] <
                containerWidth) {
                displayWidth += buttonWidthMap.current[lastDisplayIdx];
                lastDisplayIdx += 1;
            }
            setHiddenIdx(lastDisplayIdx);
        }
    }, [children, size, isReverse]);
    const classes = cx('cn-ui-m-button-group', { 'cn-ui-m-button-group-fullwidth': fullWidth }, props.className);
    const hiddenButtonRender = React.useCallback(() => {
        const hiddenButtonSheet = () => CnActionSheet.show({
            message: $i18n.get({
                id: 'HideButton',
                dm: '隐藏按钮',
                ns: 'CnButtonGroup',
            }),
            options: React.Children.map(hiddenChildren, (item) => {
                return item.props.children;
            }),
            onClick: (option, index, e) => {
                if (typeof index !== 'undefined') {
                    hiddenChildren[index].props.onClick?.(e);
                }
            },
        });
        return (hiddenIdx !== 0 && (React.createElement(CnButton, { size: size, onClick: hiddenButtonSheet }, "...")));
    }, [hiddenChildren, hiddenIdx]);
    return (React.createElement("div", { className: classes, id: containerUid },
        isReverse && hiddenButtonRender(),
        buttonChildren,
        !isReverse && hiddenButtonRender()));
};
CnButtonGroup.defaultProps = {
    fullWidth: false,
    isReverse: false,
    size: 'medium',
    shape: 'form',
};
CnButtonGroup.displayName = 'CnButtonGroup';
export { CnButtonGroup };
